import React from 'react';
import ReactMarkdown from 'react-markdown';

import { Link } from '@fly/core';
import { ScrollArea } from '@radix-ui/themes';
import classNames from 'classnames';
import 'github-markdown-css/github-markdown.css';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';

import { FlyCopyButton } from '../FlyCopyButton/FlyCopyButton';
import { FlyTable } from '../FlyTable/FlyTable';
import './FlyChatMarkdown.less';
import { extractMarkdownTableData } from './utils';

interface Props {
  markdown: string;
  className?: string;
  components?: React.ComponentProps<typeof ReactMarkdown>['components'];
  ref?: React.Ref<HTMLDivElement>;
}

export function FlyMarkdownRenderer({
  markdown,
  className,
  components,
  ref,
}: Props) {
  const markdownContent = React.useMemo(() => {
    // This regex ensures indented code blocks are treated as top-level blocks
    return markdown.replace(/^[\t]+```/gm, '```').trim();
  }, [markdown]);

  return (
    <div
      ref={ref}
      className={classNames(
        'markdown-body text-size-fly-3 !text-(--text-color)',
        className
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        rehypePlugins={[rehypeRaw, rehypeSanitize]}
        components={
          {
            p: ({
              node,
              children,
            }: {
              node?: any;
              children: React.ReactNode;
            }) => <div className='mb-2'>{children}</div>,
            code({
              node,
              className,
              children = '',
              inline,
              ...props
            }: {
              node?: any;
              className?: string;
              children: React.ReactNode;
              [key: string]: any;
              inline?: boolean;
            }) {
              const text = String(children).replace(/\n$/, '');
              const isBlock =
                node?.position?.start?.line !== node?.position?.end?.line;
              if (isBlock) {
                return (
                  <div className='flex flex-col'>
                    <FlyCopyButton
                      code={text}
                      className='mr-2 relative top-[6px] flex flex-1 justify-end'
                    />
                    <ScrollArea
                      type='auto'
                      className='p-4'
                      scrollbars='horizontal'
                    >
                      <code
                        className={classNames(
                          'text-size-fly-2! !text-(--text-color)',
                          className
                        )}
                        {...props}
                      >
                        {children}
                      </code>
                    </ScrollArea>
                  </div>
                );
              }
              return (
                <code
                  className={classNames(
                    'text-size-fly-2! !text-(--text-color)',
                    className
                  )}
                  {...props}
                >
                  {text}
                </code>
              );
            },
            dependencies({
              children,
              className,
              ...props
            }: {
              children: React.ReactNode;
              className?: string;
              [key: string]: any;
            }) {
              return (
                <span
                  className={className}
                  style={{ color: 'red', fontWeight: 'bold' }}
                  {...props}
                >
                  {children}
                </span>
              );
            },
            dependency({
              children,
              className,
              ...props
            }: {
              children: React.ReactNode;
              className?: string;
              [key: string]: any;
            }) {
              return (
                <span
                  className={className}
                  style={{ color: 'red', fontWeight: 'bold' }}
                  {...props}
                >
                  {children}
                </span>
              );
            },
            a: ({ node, ...props }: { node?: any; [key: string]: any }) => (
              <Link
                {...props}
                className={classNames(props.className)}
                target='_blank'
                rel='noopener noreferrer'
              />
            ),
            table: ({
              node,
              children,
            }: {
              node?: any;
              children: React.ReactNode;
            }) => {
              const { columns, data } = extractMarkdownTableData(children);
              if (columns.length === 0) {
                return <table>{children}</table>;
              }
              return (
                <FlyTable columns={columns} data={data} variant='surface' />
              );
            },
            ...components,
          } as any
        }
      >
        {markdownContent}
      </ReactMarkdown>
    </div>
  );
}
